import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BenefitSupplierType,
  BenefitTransactionApiService,
  BenefitTransactionGetParams,
  EmployeeBenefitState,
  GetFinancingTypePlanShowFilterRuleDto,
  NotificationType,
  SortByDirection,
  SortByEmployeeBenefitTransactionField,
} from '@flexyoo/api';
import { CartManagementMode, FinancingPlanType } from '@flexyoo/api/models';
import { FlexyooAlertsService } from '@flexyoo/common';
import { environment } from '@flexyoo/environment';
import { EmployeesBenefitChildrenChangedState } from '@flexyoo/hr/models';
import {
  DataSourceSort,
  Pager,
  PaginatedDataResponse,
  PaginatedItemsResponse,
} from '@flexyoo/shared/models';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BudgetDetail } from '../../../shared/models/funding/funding-list-dto';
import { ITransactionsTableFilter } from '../components/transactions-filters/transactions-table-filter.interface';
import {
  IEmployeeBenefitTransactionResidualValuePaidResponse,
  ITransaction,
  SendExternalProviderConfirmation,
  WorkerViewTransaction,
} from '../models';
import { mapEmployeeBenefitTransactionModel } from '../util';

@Injectable({ providedIn: 'root' })
export class TransactionsDataAccessService {
  private readonly http = inject(HttpClient);
  private readonly flexyooAlertsService = inject(FlexyooAlertsService);
  private readonly translate = inject(TranslateService);
  private readonly benefitTransactionApiService = inject(BenefitTransactionApiService);
  private readonly store = inject(Store);
  private readonly cartManagementMode = this.store.selectSignal(
    tenantSettingsSelectors.selectCartManagementMode
  );

  public getBenefitTransactions(
    filters: ITransactionsTableFilter,
    pageNumber: number,
    pageSize: number,
    sort: DataSourceSort
  ): Observable<
    PaginatedItemsResponse<ITransaction> & {
      HasImportedBenefits: boolean;
      HasBenefitsToEndWithinPeriod: boolean;
    }
  > {
    return this.benefitTransactionApiService
      .get(this.convertTransationsRequestParamsToUrlParams(filters, pageNumber, pageSize, sort))
      .pipe(
        map(
          ({
            TotalCount,
            Transactions,
            CurrentPage,
            HasImportedBenefits,
            HasBenefitsToEndWithinPeriod,
          }) => ({
            TotalCount,
            Items: (Transactions ?? []).map(mapEmployeeBenefitTransactionModel),
            CurrentPage,
            HasImportedBenefits,
            HasBenefitsToEndWithinPeriod,
          })
        )
      );
  }

  public getWorkerViewTransactions(
    pager: Pager,
    sort: DataSourceSort,
    years?: number[],
    employeeName?: string
  ): Observable<PaginatedDataResponse<WorkerViewTransaction>> {
    const params: string[] = [];
    if (employeeName) {
      params.push(`EmployeeName=${employeeName}`);
    }
    if (years?.length) {
      params.push(years.map((y) => `Years=${y}`).join('&'));
    }

    return this.http.get<PaginatedDataResponse<WorkerViewTransaction>>(
      `${environment.api}/employee-benefits/transactions/worker?PageNumber=${
        pager.pageIndex
      }&PageSize=${pager.pageSize}&SortByField=${
        sort.SortByField
      }&SortByDirection=${sort.SortByDirection}${params.length ? `&${params.join('&')}` : ''}`
    );
  }

  public exportWorkerViewTransactions(years?: number[], employeeName?: string): Observable<Blob> {
    const params: string[] = [];
    if (employeeName) {
      params.push(`EmployeeName=${employeeName}`);
    }
    if (years?.length) {
      params.push(years.map((y) => `Years=${y}`).join('&'));
    }
    return this.http.get(
      `${environment.api}/employee-benefits/transactions/worker/export${
        params.length ? `?${params.join('&')}` : ''
      }`,
      {
        responseType: 'blob',
      }
    );
  }

  public markResidualValuesAsPaid(
    employeeBenefitIds: number[]
  ): Observable<EmployeesBenefitChildrenChangedState> {
    return this.http
      .put<IEmployeeBenefitTransactionResidualValuePaidResponse[]>(
        `${environment.api}/benefit-transactions/mark-residual-value-as-paid`,
        {
          employeeBenefitIds,
        }
      )
      .pipe(
        map((result) => ({
          States: result?.map((state) => ({
            EmployeeBenefitChildId: state.BenefitId,
            State: null,
            ErrorCode: state.ErrorCode,
            ErrorMessage: state.ErrorMessage,
            FullEmployeeName: state.EmployeeName,
            EmployeeBenefitName: state.BenefitName,
            Succeeded: state.Succeeded,
          })),
        }))
      );
  }

  public approveDocument(documentId: number) {
    return this.http.put(
      `${environment.api}/employee-benefits/document/${documentId}/approve`,
      null
    );
  }

  public rejectDocument(documentId: number, disapprovedReason: string) {
    return this.http.put(`${environment.api}/employee-benefits/document/${documentId}/disapprove`, {
      disapprovedReason,
    });
  }

  public getFunding(employeeBenefitId: number): Observable<BudgetDetail> {
    return this.http.get<BudgetDetail>(`${environment.api}/employee-benefits/${employeeBenefitId}`);
  }

  public resendConfirmationToExternalProvider(tenantBenefitChildId: number): Observable<void> {
    return this.http
      .put<SendExternalProviderConfirmation>(
        `${environment.api}/tenant-benefit-children/${tenantBenefitChildId}/send-external-order`,
        {},
        { headers: { skipSuccessMessage: 'true' } }
      )
      .pipe(
        tap(this.showResendConfirmationResultInfo.bind(this)),
        map(() => void 0)
      );
  }

  public cancelEmployeeBenefit(employeeBenefitId: number, reason?: string): Observable<void> {
    return this.http.put<void>(`${environment.api}/benefit-states/cancel`, {
      employeeBenefitId,
      reason,
    });
  }

  public approveEmployeeBenefit(employeeBenefitId: number): Observable<void> {
    return this.http.put<void>(`${environment.api}/benefit-states/approve`, {
      employeeBenefitId,
    });
  }

  public deliverEmployeeBenefit(
    employeeBenefitChildrenIds: number[]
  ): Observable<EmployeesBenefitChildrenChangedState> {
    return this.http.put<EmployeesBenefitChildrenChangedState>(
      `${environment.api}/benefit-states/deliver`,
      {
        employeeBenefitChildrenIds,
      }
    );
  }

  public exportBenefitTransactions(filters: ITransactionsTableFilter): Observable<Blob> {
    return this.benefitTransactionApiService
      .export(this.convertTransationsRequestParamsToUrlParams(filters))
      .pipe(map((response) => response.body as Blob));
  }

  private showResendConfirmationResultInfo(response: SendExternalProviderConfirmation): void {
    const text = response.IsSent
      ? this.translate.instant('general.save-success')
      : (response.ErrorMessage ?? '');
    this.flexyooAlertsService.addAlert({
      text,
      type: response.IsSent ? 'success' : 'error',
    });
  }

  private convertTransationsRequestParamsToUrlParams(
    filters: ITransactionsTableFilter,
    pageNumber?: number,
    pageSize?: number,
    sort?: DataSourceSort
  ): BenefitTransactionGetParams {
    const params: BenefitTransactionGetParams = {
      EmployeeId: filters.employeeId,
      EmployeeName: filters.employeeName || undefined,
      BenefitPeriods: filters.periods?.length ? filters.periods : undefined,
      BenefitName: filters.benefitName || undefined,
      TenantGroupBenefitIds: filters.benefitCategoryIds?.length
        ? filters.benefitCategoryIds
        : undefined,
      BenefitsStatus: filters.status?.length
        ? (filters.status as unknown as EmployeeBenefitState[])
        : undefined,
      WithCancelled: filters.withCancelled,
      OnlyBenefitsToEndWithinPeriod: filters.onlyBenefitsToEndWithinPeriod,
      Years: filters.years?.length ? filters.years : undefined,
      IsAddendumApproved:
        filters.isAddendumApproved === 'true'
          ? true
          : filters.isAddendumApproved === 'false'
            ? false
            : undefined,
      NotificationTypeForTenantBenefitChild:
        filters.notificationTypeForTenantBenefitChild as unknown as NotificationType,
      SupplierTypes: filters.supplierTypes?.length
        ? (filters.supplierTypes as unknown as BenefitSupplierType[])
        : undefined,
      OnlyCashOutsidePeriod: filters.onlyCashOutsidePeriod,
      TenantId: filters.tenantId ?? undefined,
      IsPayrollSubmitted: filters.isPayrollSubmitted,
      IsTenantYearClosed: filters.isTenantYearClosed,
      IsImported: filters.isImported,
      IsResidualValuePaid:
        filters.isResidualValuePaid === 'true'
          ? true
          : filters.isResidualValuePaid === 'false'
            ? false
            : undefined,
      Ids: filters.ids?.length ? filters.ids : undefined,
      IsPaidUsingSalarySacrifice:
        filters.financingPlanType === FinancingPlanType.SalarySacrifice ? true : undefined,
      IsPaidUsingPrimesAndBonuses:
        filters.financingPlanType === FinancingPlanType.PrimesAndBonuses ? true : undefined,
      ExcludeWithOriginalId:
        this.cartManagementMode() === CartManagementMode.Cart ? false : undefined,
      CartStates: filters.cartStates?.length ? filters.cartStates : undefined,
    };

    if (pageNumber !== undefined) {
      params.PageNumber = pageNumber;
    }

    if (pageSize !== undefined) {
      params.PageSize = pageSize;
    }

    if (sort?.SortByField) {
      params.SortByField = Number(sort.SortByField) as SortByEmployeeBenefitTransactionField;
    }

    if (sort?.SortByDirection) {
      params.SortByDirection = Number(sort.SortByDirection) as SortByDirection;
    }

    return params;
  }

  public getFinancingPlanFilters(): Observable<GetFinancingTypePlanShowFilterRuleDto> {
    return this.benefitTransactionApiService.getFinancingTypePlanFilterRule();
  }
}
